import _ from "lodash";

import { variantName } from "@features/items";
import { displayDate, insertIf, schemaToCsvReport } from "@features/reports";
import { GroupCategory } from "@models/GroupCategory";
import { PurchaseOrderVariantItem } from "@models/PurchaseOrderVariant";
import { formatMoneyString, formatUtcDate } from "@utility/utilityFunctions";
import { CsvSchema } from "@utils/csv";

import { poTypeLabel } from "../helpers";

export function purchaseOrderVariantReport(
  organization,
  categories: GroupCategory[]
) {
  const { hasPurchasingAgents } = organization;

  type ColumnType = CsvSchema<PurchaseOrderVariantItem>;

  const columns: ColumnType = [
    { id: "purchaseOrder.id", label: "Purchase Order #" },
    {
      id: "purchaseOrder.type",
      label: "PO Type",
      f: (type) => poTypeLabel(type),
    },
    { id: "purchaseOrder.supplier.name", label: "Supplier" },
    { id: "purchaseOrder.supplierReference", label: "Supplier Reference" },
    { id: "purchaseOrder.invoiceNumber", label: "Invoice #" },
    ...insertIf<PurchaseOrderVariantItem>(
      hasPurchasingAgents
        ? [
            {
              id: "purchaseOrder.purchasingAgentInvoiceNumber",
              label: "Purchasing Agent Invoice Number",
            },
            {
              id: "purchaseOrder.purchasingAgentReferenceNumber",
              label: "External PO #",
            },
          ]
        : []
    ),

    { id: "purchaseOrder.purchaserName", label: "Purchaser" },
    { id: "programNames", label: "Program" },

    {
      id: "_sku",
      label: "SKU",
      f: (_, v) => v.variant.externalWarehouseId || v.variant.variantSku,
    },
    { id: "variant.item.name", label: "Name" },
    { id: "variant", label: "Variant", f: (v) => (v ? variantName(v) : "") },
    { id: "variant.item.packSize", label: "Pack Size" },
    {
      id: "variant.item.brands",
      label: "Brand",
      f: (brands) => brands.map((b) => b.name).join(", "),
    },
    ...categories.map<ColumnType[0]>((c) => ({
      id: c.id,
      label: c.name,
      f: (_, row) =>
        row.variant
          ? (row.variant.item.groups.find(
              (group) => group.groupCategory.id === c.id
            )?.name ?? "")
          : "",
    })),
    { id: "qty", label: "Qty" },
    { id: "actualCost", label: "Act. Cost/Unit", f: formatMoneyString },
    {
      id: "_total",
      label: "Total Item Cost",
      f: (_, row) => formatMoneyString(+row.actualCost * row.qty),
    },
    { id: "purchaseOrder.status", label: "PO Status", f: _.startCase },
    {
      id: "purchaseOrder.submittedAt",
      label: "Submitted Date",
      f: (d) => formatUtcDate(d),
    },
    {
      id: "purchaseOrder.inMarketDate",
      label: "In Market Date",
      f: displayDate,
    },
    ...insertIf(organization.hasBeacons, {
      id: "hasBeacons",
      label: "Has Beacons",
      f: (v) => (v ? "Yes" : "No"),
    }),
    { id: "shipmentCount", label: "Shipment Count" },
    ...insertIf(organization.usesInventory, {
      id: "isToInventory",
      label: "Is Restock",
      f: (v) => (v ? "Yes" : "No"),
    }),
    // { id: "purchaseOrder.inboundCarrier", label: "Inbound Carrier" },
    // {
    //   id: "purchaseOrder.inboundTrackingNumber",
    //   label: "Inbound Tracking Number",
    // },
  ];

  return schemaToCsvReport(columns);
}
